import { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useImmer } from 'use-immer'
import { NumericFormat } from 'react-number-format'
import { getCurrency } from 'utils/authService'
import { SpendItem } from './SpendItem'
import { ContactSpendItem } from './ContactSpendItem'
import { useLoading } from 'hooks'
import { PUSH_NOTIFICATION } from 'actions/actionTypes'
import { AddSpend } from './AddSpend'
import { EditSpendItemModal } from './EditSpendItemModal'
import cn from 'classnames'
import s from './Spend.scss'

const initialSpend = {
  totalValue: 0,
  items: [],
  companySpecificSpend: 0,
  companyContactsDto: { contacts: [], total: 0 }
}

const initialSpendItem = { name: '', value: '', date: undefined }

const Spend = ({
  makeGetRequest,
  makePostRequest,
  makePutRequest,
  makeDeleteRequest,
  isCompanySpend
}) => {
  const [isEditSpendItemModalVisible, setIsEditSpendItemModalVisible] = useState(false)
  const [spend, setSpend] = useImmer(initialSpend)
  const [itemDetails, setItemDetails] = useImmer(initialSpendItem)
  const [, withLoadingLocks] = useLoading()
  const reduxDispatch = useDispatch()

  useEffect(() => {
    ;(async () => {
      try {
        const result = await makeGetRequest()
        if (result) {
          setSpend(result)
        }
      } catch (e) {
        reduxDispatch({
          type: PUSH_NOTIFICATION,
          payload: {
            title: 'An error occurred. Please contact support',
            level: 'error'
          }
        })
      }
    })()
  }, [makeGetRequest, reduxDispatch, setSpend])

  const changeProperty = (value, property) => {
    setItemDetails(state => {
      state[property] = value
    })
  }

  const toggleEditSpendItemtModal = () => {
    setIsEditSpendItemModalVisible(!isEditSpendItemModalVisible)
    setItemDetails(initialSpendItem)
  }

  const saveDetails = async () => {
    try {
      const response = await withLoadingLocks(makePostRequest(itemDetails))
      setSpend(response)
      setItemDetails(initialSpendItem)
      reduxDispatch({
        type: PUSH_NOTIFICATION,
        payload: {
          title: 'Spend item added successfuly',
          level: 'success'
        }
      })
    } catch (e) {
      reduxDispatch({
        type: PUSH_NOTIFICATION,
        payload: {
          title: 'An error occurred. Please contact support',
          level: 'error'
        }
      })
    }
  }

  const updateItem = async itemId => {
    try {
      const response = await withLoadingLocks(makePutRequest(itemId, itemDetails))
      setSpend(response)
      reduxDispatch({
        type: PUSH_NOTIFICATION,
        payload: {
          title: 'Spend item updated successfuly',
          level: 'success'
        }
      })
    } catch (e) {
      reduxDispatch({
        type: PUSH_NOTIFICATION,
        payload: {
          title: 'An error occurred. Please contact support',
          level: 'error'
        }
      })
    }
    toggleEditSpendItemtModal()
  }

  const deleteItem = async itemId => {
    try {
      const response = await withLoadingLocks(makeDeleteRequest(itemId))
      setSpend(response)
      reduxDispatch({
        type: PUSH_NOTIFICATION,
        payload: {
          title: 'Spend item deleted successfuly',
          level: 'success'
        }
      })
    } catch (e) {
      reduxDispatch({
        type: PUSH_NOTIFICATION,
        payload: {
          title: 'An error occurred. Please contact support',
          level: 'error'
        }
      })
    }
  }

  return (
    <div>
      <section>
        {isCompanySpend ? (
          <>
            <h6 className={cn(s.title, s.companyTitle)}>
              Company Running Total:
              <NumericFormat
                thousandSeparator
                allowNegative={false}
                value={spend.totalValue}
                prefix={getCurrency().symbol}
                displayType={'text'}
              />
            </h6>
            <h6 className={cn(s.title, s.companySubtitle)}>
              Company Specific Spend:
              <NumericFormat
                thousandSeparator
                allowNegative={false}
                value={spend.companySpecificSpend}
                prefix={getCurrency().symbol}
                displayType={'text'}
              />
            </h6>
          </>
        ) : (
          <h6 className={s.title}>
            Running Total:
            <NumericFormat
              thousandSeparator
              allowNegative={false}
              value={spend.totalValue}
              prefix={getCurrency().symbol}
              displayType={'text'}
            />
          </h6>
        )}
      </section>
      <section className={s.itemsContainer}>
        {spend.items?.map(item => (
          <SpendItem
            key={item.id}
            item={item}
            setEditedItem={setItemDetails}
            toggleEditSpendItemtModal={toggleEditSpendItemtModal}
            deleteItem={deleteItem}
          />
        ))}
      </section>
      <section className={s.itemsContainer}>
        <AddSpend
          itemDetails={itemDetails}
          changeProperty={changeProperty}
          saveDetails={saveDetails}
        />
      </section>
      {isCompanySpend && (
        <>
          <section className={cn(s.itemsContainer, s.contactItemsContainer)}>
            <h6 className={s.title}>
              Contact Spend Total:
              <NumericFormat
                thousandSeparator
                allowNegative={false}
                value={spend.companyContactsDto.total}
                prefix={getCurrency().symbol}
                displayType={'text'}
              />
            </h6>
          </section>
          <section className={s.itemsContainer}>
            {spend.companyContactsDto.contacts.map(contact => (
              <ContactSpendItem key={contact.id} contact={contact} />
            ))}
          </section>
        </>
      )}
      {isEditSpendItemModalVisible && (
        <EditSpendItemModal
          toggleEditSpendItemtModal={toggleEditSpendItemtModal}
          isEditSpendItemModalVisible={isEditSpendItemModalVisible}
          editedItem={itemDetails}
          changeProperty={changeProperty}
          updateItem={updateItem}
        />
      )}
    </div>
  )
}

export default Spend
